import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, fmtDateTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RetireDialog } from "@/components/RecordEditor";
import { ModuleNav } from "./ModuleNav";
import { Plus, Search, Timer, Play, Download, Ban, Undo2, AlertTriangle, Phone } from "lucide-react";

const EMPTY = {
  case_id: "", fs_type: "Frozen section", specimen_site: "", procedure: "", surgeon: "", or_room: "",
  blocks_frozen: 1, fs_diagnosis: "", pathologist_id: "", reported_to: "", comm_method: "Phone",
  read_back_confirmed: false, notes: "",
};

const CORR_STYLE = {
  Concordant: "bg-[#134e4a] text-white",
  Discordant: "bg-red-800 text-white",
  "Deferred to permanent": "bg-sky-100 text-sky-900",
  Pending: "bg-amber-100 text-amber-900",
};

const Stat = ({ label, value, sub, tone = "" }) => (
  <div className="border border-slate-200 p-3" data-testid={`fs-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
    <div className={`num mt-0.5 text-2xl font-bold ${tone || "text-slate-900"}`}>{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);

export default function FrozenSections() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({ q: "", flag: "", correlation: "", voided: "" });
  const [cases, setCases] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY });
  const [detail, setDetail] = useState(null);
  const [voidRow, setVoidRow] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const p = { ...filters };
      Object.keys(p).forEach((k) => !p[k] && delete p[k]);
      const [list, sum] = await Promise.all([
        api.get("/frozen-sections", { params: p }),
        api.get("/frozen-sections/summary", { params: { days: 90 } }),
      ]);
      setRows(list.data);
      setSummary(sum.data);
    } catch (e) { toast.error(apiError(e)); }
  }, [filters]);

  useEffect(() => {
    api.get("/frozen-sections/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/cases", { params: { limit: 500 } }).then((r) => setCases(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const canEdit = !!meta?.can_edit;
  const set = (k) => (e) => setForm((f) => ({
    ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value,
  }));

  const create = async (e) => {
    e.preventDefault();
    if (!form.case_id) return toast.error("Pick the accessioned case first");
    setBusy("create");
    try {
      await api.post("/frozen-sections", { ...form, blocks_frozen: Number(form.blocks_frozen) || 1 });
      toast.success("Logged — clock started at the received time");
      setForm({ ...EMPTY });
      setOpen(false);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const stamp = async (row, which) => {
    setBusy(row.id);
    try {
      await api.post(`/frozen-sections/${row.id}/stamp`, null, { params: { which } });
      toast.success(which === "reported" ? "Diagnosis time stamped" : "Received time stamped");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const saveDetail = async () => {
    setBusy("detail");
    try {
      const { data } = await api.put(`/frozen-sections/${detail.id}`, {
        fs_diagnosis: detail.fs_diagnosis || "", permanent_diagnosis: detail.permanent_diagnosis || "",
        correlation: detail.correlation, discrepancy: detail.discrepancy,
        discrepancy_note: detail.discrepancy_note || "", reported_to: detail.reported_to || "",
        comm_method: detail.comm_method, read_back_confirmed: !!detail.read_back_confirmed,
        received_at: detail.received_at, reported_at: detail.reported_at || undefined,
        notes: detail.notes || "", surgeon: detail.surgeon || "", or_room: detail.or_room || "",
      });
      toast.success(data.nonconformance_id
        ? "Saved — a nonconformance was opened in CAPTraX for the major discrepancy"
        : "Saved");
      setDetail(null);
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(""); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/frozen-sections/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "PathQity_FrozenSections.csv";
      a.setAttribute("data-testid", "fs-export-anchor");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="frozen-sections-page">
      <ModuleNav
        title="Frozen Sections & IOC"
        blurb="Intraoperative consults with the receipt-to-report clock, who the result was given to, and correlation with the permanent diagnosis."
        actions={
          <>
            <Button variant="outline" onClick={exportCsv} data-testid="fs-export-button" className="h-11 border-slate-300">
              <Download size={15} className="mr-2" /> Export CSV
            </Button>
            {canEdit && (
              <Button onClick={() => setOpen(true)} data-testid="log-frozen-section-button" className="h-11 bg-[#134e4a] hover:bg-[#0f3f3c]">
                <Plus size={16} className="mr-2" /> Log frozen section
              </Button>
            )}
          </>
        }
      />

      {summary && (
        <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="fs-metrics">
          <Stat label="Volume (90 days)" value={summary.total}
                sub={`${summary.clock_running} clock running · ${summary.awaiting_correlation} awaiting correlation`} />
          <Stat label={`Within ${summary.benchmark_minutes} min`}
                value={summary.percent_within === null ? "—" : `${summary.percent_within}%`}
                sub={summary.meets_cap_target === null ? "No timed cases yet"
                  : summary.meets_cap_target ? "Meets the 90% CAP target" : "Below the 90% CAP target"}
                tone={summary.meets_cap_target === false ? "text-red-700" : "text-[#134e4a]"} />
          <Stat label="Median / average" value={summary.median_minutes === null ? "—" : `${summary.median_minutes} min`}
                sub={summary.average_minutes === null ? "" : `average ${summary.average_minutes} min · longest ${summary.longest_minutes} min`} />
          <Stat label="Concordance" value={summary.concordance_percent === null ? "—" : `${summary.concordance_percent}%`}
                sub={`${summary.discordant} discordant · ${summary.major_discrepancies} major · ${summary.deferred} deferred`}
                tone={summary.major_discrepancies > 0 ? "text-red-700" : "text-[#134e4a]"} />
        </section>
      )}

      <div className="flex flex-wrap items-center gap-2" data-testid="fs-filters">
        <div className="relative w-full sm:w-64">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                 placeholder="Accession, patient, surgeon, diagnosis" data-testid="fs-search" className="h-10 pl-9" />
        </div>
        {[["", "All"], ["running", "Clock running"], ["over", "Over benchmark"],
          ["awaiting", "Awaiting correlation"], ["discordant", "Discordant"]].map(([k, label]) => (
          <button key={k || "all"} onClick={() => setFilters((f) => ({ ...f, flag: k }))}
                  data-testid={`fs-filter-${k || "all"}`}
                  className={`h-10 border px-3 text-sm font-medium ${filters.flag === k
                    ? "border-[#134e4a] bg-[#134e4a] text-white" : "border-slate-300 bg-white text-slate-700 hover:border-[#134e4a]"}`}>
            {label}
          </button>
        ))}
        <select value={filters.voided} onChange={(e) => setFilters((f) => ({ ...f, voided: e.target.value }))}
                data-testid="fs-voided-filter" className="h-10 border border-slate-300 bg-white px-2 text-sm">
          <option value="">Active only</option>
          <option value="true">Voided only</option>
          <option value="all">Everything</option>
        </select>
      </div>

      <section className="border border-slate-200" data-testid="fs-log">
        {rows.length === 0 ? (
          <p className="px-3 py-10 text-center text-sm text-slate-500" data-testid="fs-empty">
            No frozen sections logged for this filter.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1080px] text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr className="border-b border-slate-200">
                  <th className="px-3 py-2">Case</th><th className="px-3 py-2">Patient</th>
                  <th className="px-3 py-2">Type / site</th><th className="px-3 py-2">Surgeon / OR</th>
                  <th className="px-3 py-2">Received</th><th className="px-3 py-2">Reported</th>
                  <th className="px-3 py-2 text-right">Minutes</th>
                  <th className="px-3 py-2">Communicated</th><th className="px-3 py-2">Correlation</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className={`border-b border-slate-100 row-hover hover:bg-slate-50 ${r.voided ? "opacity-60" : ""}`}
                      data-testid={`fs-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <Link to={`/cases/${r.case_id}`} className="font-mono font-semibold text-[#134e4a] hover:underline">
                        {r.accession_number}
                      </Link>
                      {r.voided && <div className="text-[11px] font-bold uppercase text-red-800">Voided</div>}
                    </td>
                    <td className="px-3 py-2 text-slate-800">{r.patient_name}</td>
                    <td className="px-3 py-2 text-slate-700">
                      {r.fs_type}{r.specimen_site ? <div className="text-xs text-slate-500">{r.specimen_site}</div> : null}
                    </td>
                    <td className="px-3 py-2 text-slate-700">
                      {r.surgeon || "—"}{r.or_room ? <div className="text-xs text-slate-500">OR {r.or_room}</div> : null}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-600">{fmtDateTime(r.received_at)}</td>
                    <td className="px-3 py-2 text-xs text-slate-600">
                      {r.reported_at ? fmtDateTime(r.reported_at)
                        : <span className="font-semibold text-amber-800">not reported</span>}
                    </td>
                    <td className={`num px-3 py-2 text-right font-bold ${r.within_benchmark === false ? "text-red-700"
                      : r.within_benchmark ? "text-[#134e4a]" : "text-slate-500"}`}>
                      {r.minutes !== null ? `${r.minutes}`
                        : r.clock_running ? <span className="inline-flex items-center gap-1 text-amber-800"><Timer size={12} />{r.elapsed_minutes}</span>
                        : "—"}
                    </td>
                    <td className="px-3 py-2 text-xs text-slate-700">
                      {r.reported_to ? (
                        <span className="inline-flex items-center gap-1">
                          <Phone size={11} className="text-slate-400" />{r.reported_to} · {r.comm_method}
                          {r.read_back_confirmed
                            ? <span className="text-[#134e4a]"> read-back ✓</span>
                            : <span className="text-amber-800"> no read-back</span>}
                        </span>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-1.5 py-0.5 text-[11px] font-semibold ${CORR_STYLE[r.correlation] || "bg-slate-200"}`}>
                        {r.correlation}
                      </span>
                      {r.discrepancy === "Major" && (
                        <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-bold text-red-800">
                          <AlertTriangle size={11} /> major
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <span className="flex flex-wrap justify-end gap-1.5">
                        {canEdit && !r.voided && r.clock_running && (
                          <Button size="sm" disabled={busy === r.id} onClick={() => stamp(r, "reported")}
                                  data-testid={`fs-report-${r.id}`} className="h-9 bg-[#134e4a] hover:bg-[#0f3f3c]">
                            <Play size={13} className="mr-1" /> Reported now
                          </Button>
                        )}
                        {canEdit && !r.voided && (
                          <Button size="sm" variant="outline" onClick={() => setDetail({ ...r })}
                                  data-testid={`fs-edit-${r.id}`} className="h-9 border-slate-300">Open</Button>
                        )}
                        {canEdit && !r.voided && user?.role !== "tech" && user?.role !== "pathologist_assistant" && (
                          <Button size="sm" variant="outline" onClick={() => setVoidRow(r)}
                                  data-testid={`fs-void-${r.id}`} className="h-9 border-slate-300">
                            <Ban size={13} />
                          </Button>
                        )}
                        {r.voided && canEdit && (
                          <Button size="sm" variant="outline" data-testid={`fs-reinstate-${r.id}`} className="h-9 border-slate-300"
                                  onClick={async () => {
                                    try { await api.post(`/frozen-sections/${r.id}/reinstate`); toast.success("Reinstated"); load(); }
                                    catch (e) { toast.error(apiError(e)); }
                                  }}>
                            <Undo2 size={13} />
                          </Button>
                        )}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* log a new frozen section */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="fs-create-dialog">
          <DialogHeader><DialogTitle className="font-display">Log frozen section / intraoperative consult</DialogTitle></DialogHeader>
          <form onSubmit={create} className="grid gap-3 sm:grid-cols-2" data-testid="fs-create-form">
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Accessioned case</Label>
              <select value={form.case_id} onChange={set("case_id")} data-testid="fs-case-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="">Select case…</option>
                {cases.map((c) => (
                  <option key={c.id} value={c.id}>{`${c.accession_number} — ${c.patient_name}`}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Type</Label>
              <select value={form.fs_type} onChange={set("fs_type")} data-testid="fs-type-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                {(meta?.fs_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Specimen site</Label>
              <Input value={form.specimen_site} onChange={set("specimen_site")} data-testid="fs-site-input" className="mt-1 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Procedure</Label>
              <Input value={form.procedure} onChange={set("procedure")} data-testid="fs-procedure-input" className="mt-1 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Surgeon</Label>
              <Input value={form.surgeon} onChange={set("surgeon")} data-testid="fs-surgeon-input" className="mt-1 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">OR room</Label>
              <Input value={form.or_room} onChange={set("or_room")} data-testid="fs-or-input" className="mt-1 h-11 border-slate-300" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-600">Blocks frozen</Label>
              <Input type="number" min="1" max="20" value={form.blocks_frozen} onChange={set("blocks_frozen")}
                     data-testid="fs-blocks-input" className="mt-1 h-11 border-slate-300" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wide text-slate-600">Pathologist</Label>
              <select value={form.pathologist_id} onChange={set("pathologist_id")} data-testid="fs-pathologist-select"
                      className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                <option value="">Use the case pathologist</option>
                {(meta?.pathologists || []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <p className="sm:col-span-2 border-l-4 border-[#134e4a] bg-teal-50 px-3 py-2 text-xs text-slate-700">
              The receipt clock starts now. Press <strong>Reported now</strong> on the row when the diagnosis is
              given to the OR — both times stay editable afterwards.
            </p>
            <Button type="submit" disabled={busy === "create"} data-testid="fs-create-submit"
                    className="h-11 sm:col-span-2 bg-[#134e4a] hover:bg-[#0f3f3c]">
              {busy === "create" ? "Logging…" : "Start clock and log"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* detail: diagnosis, communication, correlation */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto" data-testid="fs-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">
              {detail?.fs_type} — <span className="font-mono">{detail?.accession_number}</span>
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Received</Label>
                <Input type="datetime-local" data-testid="fs-detail-received"
                       value={(detail.received_at || "").slice(0, 16)}
                       onChange={(e) => setDetail((d) => ({ ...d, received_at: e.target.value }))}
                       className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Diagnosis reported</Label>
                <Input type="datetime-local" data-testid="fs-detail-reported"
                       value={(detail.reported_at || "").slice(0, 16)}
                       onChange={(e) => setDetail((d) => ({ ...d, reported_at: e.target.value }))}
                       className="mt-1 h-11 border-slate-300" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Frozen section diagnosis</Label>
                <Textarea value={detail.fs_diagnosis || ""} data-testid="fs-detail-diagnosis"
                          onChange={(e) => setDetail((d) => ({ ...d, fs_diagnosis: e.target.value }))}
                          className="mt-1 min-h-[80px] border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Result given to</Label>
                <Input value={detail.reported_to || ""} data-testid="fs-detail-reported-to"
                       onChange={(e) => setDetail((d) => ({ ...d, reported_to: e.target.value }))}
                       placeholder="Surgeon or OR staff name" className="mt-1 h-11 border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">How</Label>
                <select value={detail.comm_method} data-testid="fs-detail-method"
                        onChange={(e) => setDetail((d) => ({ ...d, comm_method: e.target.value }))}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.comm_methods || []).map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 sm:col-span-2">
                <input type="checkbox" checked={!!detail.read_back_confirmed} data-testid="fs-detail-readback"
                       onChange={(e) => setDetail((d) => ({ ...d, read_back_confirmed: e.target.checked }))}
                       className="h-4 w-4 accent-[#134e4a]" />
                Read-back confirmed by the receiver
              </label>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Permanent diagnosis</Label>
                <Textarea value={detail.permanent_diagnosis || ""} data-testid="fs-detail-permanent"
                          onChange={(e) => setDetail((d) => ({ ...d, permanent_diagnosis: e.target.value }))}
                          className="mt-1 min-h-[80px] border-slate-300" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Correlation</Label>
                <select value={detail.correlation} data-testid="fs-detail-correlation"
                        onChange={(e) => setDetail((d) => ({ ...d, correlation: e.target.value }))}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.correlations || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-600">Discrepancy</Label>
                <select value={detail.discrepancy} data-testid="fs-detail-discrepancy"
                        onChange={(e) => setDetail((d) => ({ ...d, discrepancy: e.target.value }))}
                        className="mt-1 h-11 w-full rounded-md border border-slate-300 bg-white px-3 text-sm">
                  {(meta?.discrepancies || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              {detail.correlation === "Discordant" && (
                <div className="sm:col-span-2">
                  <Label className="text-xs uppercase tracking-wide text-slate-600">Discrepancy note</Label>
                  <Textarea value={detail.discrepancy_note || ""} data-testid="fs-detail-discrepancy-note"
                            onChange={(e) => setDetail((d) => ({ ...d, discrepancy_note: e.target.value }))}
                            className="mt-1 min-h-[70px] border-slate-300" />
                  {detail.discrepancy === "Major" && (
                    <p className="mt-1 text-xs font-semibold text-red-800">
                      Saving a major discrepancy opens a nonconformance in CAPTraX automatically.
                    </p>
                  )}
                </div>
              )}
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-wide text-slate-600">Notes</Label>
                <Textarea value={detail.notes || ""} data-testid="fs-detail-notes"
                          onChange={(e) => setDetail((d) => ({ ...d, notes: e.target.value }))}
                          className="mt-1 min-h-[60px] border-slate-300" />
              </div>
              <Button onClick={saveDetail} disabled={busy === "detail"} data-testid="fs-detail-save"
                      className="h-11 sm:col-span-2 bg-[#134e4a] hover:bg-[#0f3f3c]">
                {busy === "detail" ? "Saving…" : "Save"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <RetireDialog
        open={!!voidRow} onOpenChange={(o) => !o && setVoidRow(null)}
        title="Void frozen section record"
        description="The record stays in the log with the reason attached for the inspector."
        confirmLabel="Void record"
        testid="fs-void-dialog"
        onConfirm={async (reason) => {
          await api.post(`/frozen-sections/${voidRow.id}/void`, { reason });
          toast.success("Voided");
          setVoidRow(null);
          load();
        }}
      />
    </div>
  );
}
